import { Observable, ReplaySubject, tap } from 'rxjs';
import { GedFile } from '../model/ged-file';

type GalleryManagerQueue = {
  size: { width: number; height: number };
  gedFile: GedFile;
};

export class GalleryManager {
  public delayBetweenPics = 100; // ms
  private queue: GalleryManagerQueue[] = [];

  private gedFileLoaded$$ = new ReplaySubject<GedFile>();
  public gedFileLoaded$: Observable<GedFile> =
    this.gedFileLoaded$$.asObservable();

  private isLoading = false;

  constructor(delayBetweenPics?: number) {
    if (delayBetweenPics !== undefined) {
      this.delayBetweenPics = delayBetweenPics;
    }
  }

  public addPic(
    pic: GedFile,
    onTop = false,
    size?: { width: number; height: number },
  ) {
    // If no id, don't load the thumbnail
    if (!pic.metadata) {
      return;
    }

    if (size) {
      // Need to round the size to avoid a 1px difference
      size.width = Math.round(size.width);
      size.height = Math.round(size.height);
    }
    // Don't load the thumbnail if we already have the base64
    if (size && pic.thumbnailBase64) {
      return;
    }
    // Don't load the base64 if we already have the base64
    if (!size && pic.base64) {
      return;
    }

    if (this.isInQueue(pic, size)) {
      return;
    }
    if (onTop) {
      this.queue.unshift({ size: size, gedFile: pic });
      this.startLoading();
      return;
    }
    this.queue.push({ size: size, gedFile: pic });
    this.startLoading();
  }
  public addPics(pics: GedFile[], size?: { width: number; height: number }) {
    pics.forEach((p) => this.addPic(p, false, size));
  }

  public clear() {
    this.queue = [];
  }

  private startLoading() {
    if (this.queue.length === 0) {
      return;
    }
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.loadPic(this.queue.at(0));
  }

  private next() {
    if (this.queue.length === 0) {
      this.isLoading = false;
      return;
    }
    setTimeout(() => {
      this.loadPic(this.queue.at(0));
    }, this.delayBetweenPics);
  }

  private loadPic(queueElement: GalleryManagerQueue) {
    this.loadPic$(queueElement)
      .pipe(tap(() => this.gedFileLoaded$$.next(queueElement.gedFile)))
      .subscribe({
        error: (error) => {
          console.warn('Failed to load gallery picture', {
            id: queueElement.gedFile.id,
            metadata: queueElement.gedFile.metadata,
            endpoint: queueElement.gedFile.endpoint,
            size: queueElement.size,
            error,
          });
        },
        complete: () => {
          const index = this.queue.findIndex(
            (p) => p.gedFile.id === queueElement.gedFile.id,
          );
          if (index >= 0) {
            this.queue.splice(index, 1);
          }
          this.next();
        },
      });
  }

  private loadPic$(queueElement: GalleryManagerQueue): Observable<void> {
    return new Observable<void>((observer) => {
      let src = queueElement.gedFile.endpoint;
      if (queueElement.size) {
        src = `${queueElement.gedFile.endpoint}?width=${queueElement.size.width}&height=${queueElement.size.height}`;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        if (!queueElement.size) {
          queueElement.gedFile.base64 = canvas.toDataURL();
        } else {
          queueElement.gedFile.thumbnailBase64 = canvas.toDataURL();
        }
        observer.next();
        observer.complete();
      };
      img.onerror = () => observer.error('Erreur de chargement');
    });
  }

  private isInQueue(
    pic: GedFile,
    size?: { width: number; height: number },
  ): boolean {
    const index = this.queue.findIndex((p) => p.gedFile.id === pic.id);
    if (index === -1) {
      return false;
    }
    const queueElement = this.queue[index];
    if (queueElement.size && size) {
      return (
        queueElement.size.width === size.width &&
        queueElement.size.height === size.height
      );
    }
    return true;
  }
}
